import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { nomenclatorV2Api, LateralityPayload, PurposePayload } from '../../services/nomenclatorV2Api'

interface Laterality {
  id: string
  display_name_en: string
  display_name_ro: string
  applies_to_regions?: string[]
}

interface Purpose {
  id: string
  display_name_en: string
  display_name_ro: string
  description?: string
}

export const LateralitiesAndPurposesManager: React.FC = () => {
  const [lateralities, setLateralities] = useState<Laterality[]>([])
  const [purposes, setPurposes] = useState<Purpose[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Laterality state
  const [editingLateralityId, setEditingLateralityId] = useState<string | null>(null)
  const [deleteLateralityDialogOpen, setDeleteLateralityDialogOpen] = useState(false)
  const [lateralityToDelete, setLateralityToDelete] = useState<string | null>(null)
  const [lateralityForm, setLateralityForm] = useState<LateralityPayload>({
    id: '',
    display_name_en: '',
    display_name_ro: ''
  })

  // Purpose state
  const [editingPurposeId, setEditingPurposeId] = useState<string | null>(null)
  const [deletePurposeDialogOpen, setDeletePurposeDialogOpen] = useState(false)
  const [purposeToDelete, setPurposeToDelete] = useState<string | null>(null)
  const [purposeForm, setPurposeForm] = useState<PurposePayload>({
    id: '',
    display_name_en: '',
    display_name_ro: '',
    description: ''
  })

  const loadLateralities = async () => {
    try {
      const data = await nomenclatorV2Api.listLateralities()
      setLateralities(data)
    } catch (err) {
      console.error('Failed to load lateralities', err)
      setError('Nu am putut încărca lateralitățile')
    }
  }

  const loadPurposes = async () => {
    try {
      const data = await nomenclatorV2Api.listPurposes()
      setPurposes(data)
    } catch (err) {
      console.error('Failed to load purposes', err)
      setError('Nu am putut încărca purpose-urile')
    }
  }

  useEffect(() => {
    setLoading(true)
    Promise.all([loadLateralities(), loadPurposes()]).finally(() => setLoading(false))
  }, [])

  // Laterality handlers
  const handleLateralitySubmit = async () => {
    if (!lateralityForm.id || !lateralityForm.display_name_ro) {
      setError('ID și Nume RO sunt obligatorii')
      return
    }

    try {
      if (editingLateralityId) {
        await nomenclatorV2Api.updateLaterality(editingLateralityId, {
          display_name_en: lateralityForm.display_name_en,
          display_name_ro: lateralityForm.display_name_ro
        })
        setSuccess('Lateralitate actualizată cu succes')
      } else {
        await nomenclatorV2Api.createLaterality({
          id: lateralityForm.id,
          display_name_en: lateralityForm.display_name_en,
          display_name_ro: lateralityForm.display_name_ro
        })
        setSuccess('Lateralitate adăugată cu succes')
      }
      resetLateralityForm()
      loadLateralities()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la salvare')
    }
  }

  const handleLateralityEdit = (laterality: Laterality) => {
    setLateralityForm({
      id: laterality.id,
      display_name_en: laterality.display_name_en,
      display_name_ro: laterality.display_name_ro
    })
    setEditingLateralityId(laterality.id)
  }

  const handleLateralityDeleteClick = (id: string) => {
    setLateralityToDelete(id)
    setDeleteLateralityDialogOpen(true)
  }

  const handleLateralityDeleteConfirm = async () => {
    if (!lateralityToDelete) return

    try {
      await nomenclatorV2Api.deleteLaterality(lateralityToDelete)
      setSuccess('Lateralitate ștearsă cu succes')
      loadLateralities()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la ștergere')
    } finally {
      setDeleteLateralityDialogOpen(false)
      setLateralityToDelete(null)
    }
  }

  const resetLateralityForm = () => {
    setLateralityForm({
      id: '',
      display_name_en: '',
      display_name_ro: ''
    })
    setEditingLateralityId(null)
  }

  // Purpose handlers
  const handlePurposeSubmit = async () => {
    if (!purposeForm.id || !purposeForm.display_name_ro) {
      setError('ID și Nume RO sunt obligatorii')
      return
    }

    try {
      if (editingPurposeId) {
        await nomenclatorV2Api.updatePurpose(editingPurposeId, {
          display_name_en: purposeForm.display_name_en,
          display_name_ro: purposeForm.display_name_ro,
          description: purposeForm.description
        })
        setSuccess('Purpose actualizat cu succes')
      } else {
        await nomenclatorV2Api.createPurpose({
          id: purposeForm.id,
          display_name_en: purposeForm.display_name_en,
          display_name_ro: purposeForm.display_name_ro,
          description: purposeForm.description
        })
        setSuccess('Purpose adăugat cu succes')
      }
      resetPurposeForm()
      loadPurposes()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la salvare')
    }
  }

  const handlePurposeEdit = (purpose: Purpose) => {
    setPurposeForm({
      id: purpose.id,
      display_name_en: purpose.display_name_en,
      display_name_ro: purpose.display_name_ro,
      description: purpose.description || ''
    })
    setEditingPurposeId(purpose.id)
  }

  const handlePurposeDeleteClick = (id: string) => {
    setPurposeToDelete(id)
    setDeletePurposeDialogOpen(true)
  }

  const handlePurposeDeleteConfirm = async () => {
    if (!purposeToDelete) return

    try {
      await nomenclatorV2Api.deletePurpose(purposeToDelete)
      setSuccess('Purpose șters cu succes')
      loadPurposes()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la ștergere')
    } finally {
      setDeletePurposeDialogOpen(false)
      setPurposeToDelete(null)
    }
  }

  const resetPurposeForm = () => {
    setPurposeForm({
      id: '',
      display_name_en: '',
      display_name_ro: '',
      description: ''
    })
    setEditingPurposeId(null)
  }

  if (loading && lateralities.length === 0 && purposes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  // Compact input styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem', height: 32 },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' }
  }
  const tableSx = {
    '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' },
    '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 },
    '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
  }
  const dialogPaperSx = { bgcolor: 'background.paper', backgroundImage: 'none' }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.78rem' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess(null)} message={success} />

      {/* Lateralities Section - Compact */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AddIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {editingLateralityId ? 'Editează Lateralitate' : 'Adaugă Lateralitate Nouă'}
            </Typography>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <TextField
              label="Cod (EN)"
              value={lateralityForm.id}
              onChange={(e) => setLateralityForm({ ...lateralityForm, id: e.target.value.toUpperCase() })}
              disabled={!!editingLateralityId}
              required
              size="small"
              sx={{ width: 120, ...inputSx }}
            />
            <TextField
              label="Nume RO"
              value={lateralityForm.display_name_ro}
              onChange={(e) => setLateralityForm({ ...lateralityForm, display_name_ro: e.target.value })}
              required
              size="small"
              sx={{ flex: 1, minWidth: 150, ...inputSx }}
            />
            <TextField
              label="Nume EN"
              value={lateralityForm.display_name_en}
              onChange={(e) => setLateralityForm({ ...lateralityForm, display_name_en: e.target.value })}
              size="small"
              sx={{ flex: 1, minWidth: 150, ...inputSx }}
            />
            <Button 
              variant="contained" 
              onClick={handleLateralitySubmit} 
              disabled={!lateralityForm.id || !lateralityForm.display_name_ro}
              size="small"
              sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
            >
              {editingLateralityId ? 'Salvează' : 'Adaugă'}
            </Button>
            {editingLateralityId && (
              <Button variant="outlined" onClick={resetLateralityForm} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>
                Anulează
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 1.5, ...tableSx }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
            Lateralități ({lateralities.length})
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 } }}>
                <TableCell>Cod</TableCell>
                <TableCell>Nume RO</TableCell>
                <TableCell>Nume EN</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lateralities.map((laterality) => (
                <TableRow key={laterality.id} hover>
                  <TableCell><strong>{laterality.id}</strong></TableCell>
                  <TableCell>{laterality.display_name_ro}</TableCell>
                  <TableCell>{laterality.display_name_en}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleLateralityEdit(laterality)} color="primary"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleLateralityDeleteClick(laterality.id)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      {/* Purposes Section - Compact */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AddIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {editingPurposeId ? 'Editează Purpose' : 'Adaugă Purpose Nou'}
            </Typography>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <TextField
              label="Cod (EN)"
              value={purposeForm.id}
              onChange={(e) => setPurposeForm({ ...purposeForm, id: e.target.value })}
              disabled={!!editingPurposeId}
              required
              size="small"
              sx={{ width: 120, ...inputSx }}
            />
            <TextField
              label="Nume RO"
              value={purposeForm.display_name_ro}
              onChange={(e) => setPurposeForm({ ...purposeForm, display_name_ro: e.target.value })}
              required
              size="small"
              sx={{ flex: 1, minWidth: 150, ...inputSx }}
            />
            <TextField
              label="Nume EN"
              value={purposeForm.display_name_en}
              onChange={(e) => setPurposeForm({ ...purposeForm, display_name_en: e.target.value })}
              size="small"
              sx={{ flex: 1, minWidth: 150, ...inputSx }}
            />
            <TextField
              label="Descriere"
              value={purposeForm.description}
              onChange={(e) => setPurposeForm({ ...purposeForm, description: e.target.value })}
              size="small"
              sx={{ flex: 2, minWidth: 200, ...inputSx }}
            />
            <Button 
              variant="contained" 
              onClick={handlePurposeSubmit} 
              disabled={!purposeForm.id || !purposeForm.display_name_ro}
              size="small"
              sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
            >
              {editingPurposeId ? 'Salvează' : 'Adaugă'}
            </Button>
            {editingPurposeId && (
              <Button variant="outlined" onClick={resetPurposeForm} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>
                Anulează
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card sx={tableSx}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
            Purpose-uri ({purposes.length})
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 } }}>
                <TableCell>Cod</TableCell>
                <TableCell>Nume RO</TableCell>
                <TableCell>Nume EN</TableCell>
                <TableCell>Descriere</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purposes.map((purpose) => (
                <TableRow key={purpose.id} hover>
                  <TableCell><strong>{purpose.id}</strong></TableCell>
                  <TableCell>{purpose.display_name_ro}</TableCell>
                  <TableCell>{purpose.display_name_en}</TableCell>
                  <TableCell>{purpose.description || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handlePurposeEdit(purpose)} color="primary"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handlePurposeDeleteClick(purpose.id)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Dialogs - Compact */}
      <Dialog open={deleteLateralityDialogOpen} onClose={() => setDeleteLateralityDialogOpen(false)} PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
          Confirmare ștergere
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.85rem' }}>Sigur vrei să ștergi lateralitatea <strong>{lateralityToDelete}</strong>?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setDeleteLateralityDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>Anulează</Button>
          <Button onClick={handleLateralityDeleteConfirm} color="error" variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>Șterge</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deletePurposeDialogOpen} onClose={() => setDeletePurposeDialogOpen(false)} PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
          Confirmare ștergere
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.85rem' }}>Sigur vrei să ștergi purpose-ul <strong>{purposeToDelete}</strong>?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setDeletePurposeDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>Anulează</Button>
          <Button onClick={handlePurposeDeleteConfirm} color="error" variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>Șterge</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

